import { useQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { api } from '../../../lib/api.js';

interface Bucket {
  ts_bucket: number;
  bot_label: string;
  traffic_class: string;
  requests: number;
  cache_served: number;
  errors: number;
}

export function AiVisibilityPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const now = Date.now();
  const from = now - 24 * 60 * 60 * 1000;
  const { data, isPending } = useQuery({
    queryKey: ['raven', 'overview', id, from, now],
    queryFn: () =>
      api.get<{ buckets: Bucket[] }>(
        `/api/raven/v1/visibility/overview?workspace_id=${encodeURIComponent(id)}&from=${from}&to=${now}`,
      ),
    refetchInterval: 30_000,
  });

  const grouped = (data?.buckets ?? []).reduce<Record<string, Bucket>>((acc, b) => {
    const k = new Date(b.ts_bucket).toLocaleTimeString();
    if (!acc[k]) acc[k] = { ...b, ts_bucket: b.ts_bucket };
    else {
      acc[k].requests += b.requests;
      acc[k].cache_served += b.cache_served;
      acc[k].errors += b.errors;
    }
    return acc;
  }, {});
  const series = Object.entries(grouped).map(([k, v]) => ({ name: k, ...v }));

  const totals = (data?.buckets ?? []).reduce(
    (a, b) => ({
      requests: a.requests + b.requests,
      cache_served: a.cache_served + b.cache_served,
      errors: a.errors + b.errors,
    }),
    { requests: 0, cache_served: 0, errors: 0 },
  );

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Kpi label="Requests (24h)" value={totals.requests.toLocaleString()} />
        <Kpi label="Cache served" value={totals.cache_served.toLocaleString()} />
        <Kpi label="Errors" value={totals.errors.toLocaleString()} />
      </div>
      <div className="card">
        <h3 className="text-base font-semibold">Hits per 5-minute bucket</h3>
        <p className="mt-1 text-xs text-slate-500">
          Source: RAVEN events rolled up in raven D1 (
          <code>raven_events_rollup</code>).
        </p>
        <div className="mt-4 h-72">
          {isPending ? (
            <div className="text-sm text-slate-400">Loading…</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={series}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="requests" stackId="a" fill="#2563eb" />
                <Bar dataKey="cache_served" stackId="a" fill="#16a34a" />
                <Bar dataKey="errors" stackId="a" fill="#dc2626" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <div className="card">
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-2 text-2xl font-semibold">{value}</div>
    </div>
  );
}
